import { type ReactNode, useState } from 'react';

import { Link } from '@tanstack/react-router';
import { ExternalLinkIcon } from 'lucide-react';

import {
  Icon,
  PrimaryLink,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
  Text,
  Tooltip,
} from '@/_components';
import { CellFallback, CellName, TableHeading } from '@/_containers';
import { useGetEmailHistoriesByInvoiceId } from '@/_pages/settings/email-history/shared/apis';
import { getEmailHistoryRecipients } from '@/_pages/settings/email-history/shared/get-email-history-recipients';
import { buildInvoiceEmailHistoryListFilterStr } from '@/_pages/settings/email-history/shared/invoice-email-history-filter';
import { getDeliveryStatusLabel } from '@/_pages/settings/email-history/shared/model';
import type { EmailHistoryQuerySchema } from '@/_pages/settings/email-history/shared/schemas/email-history-query-schema';
import {
  formatEmailHistoryScheduledCell,
  formatEmailHistorySentCell,
} from '@/_pages/settings/email-history/shared/utils/format-email-history-sent-or-scheduled';
import { nameResolver } from '@/_shared';

import { InvoiceEmailRecipientsList } from './invoice-email-recipients-list';

import type { InvoiceQuerySchema } from '../schemas/invoices/invoice-query-schema';

const TOOLTIP_WIDTH_PX = 1040;

const compactTableHeadClass = '!h-7 !py-0 px-1.5 align-middle';
const compactTableHeadTextClass = 'text-[10px] leading-none font-semibold';

type InvoiceEmailHistoryTooltipProps = {
  invoice: InvoiceQuerySchema;
  canLoadHistory: boolean;
  tooltipWhenDisabled?: string;
  children: ReactNode;
};

function TooltipUserCell({ row }: { row: EmailHistoryQuerySchema }) {
  const senderName = row.sender?.name ?? nameResolver(row.sender?.first_name, row.sender?.last_name, '—');
  const senderId = row.sender?.id;

  if (!senderId) {
    return <CellFallback text={senderName} className="text-xs" />;
  }

  return (
    <CellName
      name={senderName}
      src={row.sender?.profile_images?.at(0)?.url}
      to="/system-users/$id"
      params={{ id: `${senderId}` }}
      state={{ naturalBack: true }}
      className="text-xs"
      avatarClassName="size-5"
    />
  );
}

function InvoiceEmailHistoryTooltipContent({
  invoice,
  histories,
  isLoading,
  isError,
}: {
  invoice: InvoiceQuerySchema;
  histories: EmailHistoryQuerySchema[];
  isLoading: boolean;
  isError: boolean;
}) {
  const filterStr = buildInvoiceEmailHistoryListFilterStr(invoice.id);
  const countLabel = isLoading ? '…' : histories.length;

  return (
    <div
      className="pointer-events-auto space-y-1.5 px-2 py-1"
      style={{ width: TOOLTIP_WIDTH_PX, maxWidth: 'min(1040px, 95vw)' }}
    >
      <div className="border-c-border-muted text-c-card-foreground border-b px-0.5 pb-1 text-xs leading-tight font-semibold">
        Email history ({countLabel})
      </div>

      <div className="max-h-52 min-h-10 overflow-y-auto">
        {isLoading ? (
          <div className="space-y-1 py-0.5">
            <Skeleton className="h-6 w-full" />
            <Skeleton className="h-6 w-full" />
            <Skeleton className="h-6 w-4/5" />
          </div>
        ) : isError ? (
          <Text className="text-c-card-muted text-xs">Unable to load email history.</Text>
        ) : histories.length === 0 ? (
          <Text className="text-c-card-muted text-xs">No email history for this invoice.</Text>
        ) : (
          <Table className="text-xs">
            <TableHeader flatRoundedCorners className="bg-c-background-muted [&_th]:h-7!">
              <TableRow className="border-c-border-muted hover:bg-transparent">
                <TableHeading
                  title="Recipients"
                  headerClassName={compactTableHeadClass}
                  className={compactTableHeadTextClass}
                />
                <TableHeading
                  title="Scheduled"
                  headerClassName={compactTableHeadClass}
                  className={compactTableHeadTextClass}
                />
                <TableHeading
                  title="Sent"
                  headerClassName={compactTableHeadClass}
                  className={compactTableHeadTextClass}
                />
                <TableHeading
                  title="Status"
                  headerClassName={compactTableHeadClass}
                  className={compactTableHeadTextClass}
                />
                <TableHeading
                  title="User"
                  headerClassName={`min-w-24 ${compactTableHeadClass}`}
                  className={compactTableHeadTextClass}
                />
                <TableHeading
                  title=""
                  headerClassName={`w-7 ${compactTableHeadClass}`}
                  className={compactTableHeadTextClass}
                />
              </TableRow>
            </TableHeader>
            <TableBody>
              {histories.map((row) => {
                const recipients = getEmailHistoryRecipients(row, invoice);
                return (
                  <TableRow key={row.id} className="border-c-border-muted hover:bg-transparent">
                    <TableCell className="max-w-80 min-w-52 px-1.5 py-1 align-top">
                      {recipients.length === 0 ? (
                        <Text className="text-c-card-muted text-xs">—</Text>
                      ) : (
                        <InvoiceEmailRecipientsList
                          recipients={recipients}
                          showHeading={false}
                          compact
                          className="col-span-12 space-y-0!"
                        />
                      )}
                    </TableCell>
                    <TableCell className="text-c-card-muted max-w-56 px-1.5 py-1 align-top text-[11px] leading-snug">
                      {formatEmailHistoryScheduledCell(row)}
                    </TableCell>
                    <TableCell className="text-c-card-muted max-w-56 px-1.5 py-1 align-top text-[11px] leading-snug">
                      {formatEmailHistorySentCell(row)}
                    </TableCell>
                    <TableCell className="px-1.5 py-1 align-top whitespace-nowrap">
                      <Text className="text-xxs text-c-card-muted">{getDeliveryStatusLabel(row.delivery_status)}</Text>
                    </TableCell>
                    <TableCell className="min-w-28 px-1.5 py-1 align-top">
                      <TooltipUserCell row={row} />
                    </TableCell>
                    <TableCell className="w-7 px-1 py-1 align-top">
                      <PrimaryLink
                        to="/settings/email-history/$id"
                        params={{ id: `${row.id}` }}
                        state={{ naturalBack: true }}
                        target="_blank"
                        className="inline-flex"
                        aria-label={`View email history #${row.id}`}
                      >
                        <Icon icon={ExternalLinkIcon} className="size-3.5" />
                      </PrimaryLink>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </div>

      <div className="border-c-border-muted border-t px-0.5 pt-1">
        <Link
          to="/settings/email-history"
          search={{ filter_str: filterStr }}
          className="text-primary text-xs font-medium hover:underline"
        >
          View all email history
        </Link>
      </div>
    </div>
  );
}

export function InvoiceEmailHistoryTooltip({
  invoice,
  canLoadHistory,
  tooltipWhenDisabled,
  children,
}: InvoiceEmailHistoryTooltipProps) {
  const [fetchEnabled, setFetchEnabled] = useState(false);

  const {
    data: histories = [],
    isLoading,
    isError,
  } = useGetEmailHistoriesByInvoiceId(invoice.id, {
    enabled: fetchEnabled && canLoadHistory,
  });

  const content = !canLoadHistory ? (
    <Text className="text-c-card-foreground max-w-xs text-xs">{tooltipWhenDisabled}</Text>
  ) : (
    <InvoiceEmailHistoryTooltipContent
      invoice={invoice}
      histories={histories}
      isLoading={isLoading && fetchEnabled}
      isError={isError}
    />
  );

  return (
    <Tooltip
      content={content}
      placement="top"
      zIndex={50}
      interactive={canLoadHistory}
      trigger="mouseenter focus"
      appendTo={() => document.body}
      delay={[150, 50]}
      maxWidth={TOOLTIP_WIDTH_PX}
      onShow={() => {
        if (canLoadHistory) {
          setFetchEnabled(true);
        }
      }}
    >
      <span className="inline-flex">{children}</span>
    </Tooltip>
  );
}
